import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";

const UI_CURRENCY = "LKR";

const n = (value) => Number(value || 0);

function money(value) {
  return `${UI_CURRENCY} ${n(value).toLocaleString("en-LK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(value) {
  const date = value?.toDate?.() || value;
  if (!date) return "-";
  try {
    return new Date(date).toLocaleString();
  } catch {
    return "-";
  }
}

function normalizePayoutType(value = "") {
  return String(value || "").toLowerCase() === "renewal" ? "renewal" : "first";
}

function shopLabel(row = {}, userMap = {}) {
  const user = userMap[row.instructorId] || {};
  return (
    user.fullName ||
    user.displayName ||
    user.name ||
    row.instructorSnapshot?.name ||
    row.instructorName ||
    user.email ||
    row.instructorEmail ||
    row.instructorId ||
    "Unknown shop owner"
  );
}

function shopEmail(row = {}, userMap = {}) {
  const user = userMap[row.instructorId] || {};
  return user.email || row.instructorSnapshot?.email || row.instructorEmail || "";
}

function statusTone(status = "") {
  const value = String(status || "").toLowerCase();
  if (value === "approved") return "bg-green-100 text-green-700";
  if (value === "declined") return "bg-rose-100 text-rose-700";
  if (value === "pending") return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
}

function payoutTone(type = "") {
  return type === "renewal"
    ? "bg-sky-100 text-sky-700"
    : "bg-violet-100 text-violet-700";
}

function StatCard({ label, value, helper = "", tone = "text-slate-900" }) {
  return (
    <div className="rounded-3xl border bg-white p-5 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
        {label}
      </div>
      <div className={`mt-3 text-3xl font-black ${tone}`}>{value}</div>
      {helper ? <div className="mt-2 text-sm text-slate-500">{helper}</div> : null}
    </div>
  );
}

export default function AdminPackageSalesSummary() {
  const [orders, setOrders] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "instructor_package_orders"), orderBy("createdAt", "desc")),
      (snap) => setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      setUserMap(
        Object.fromEntries(snap.docs.map((entry) => [entry.id, { id: entry.id, ...entry.data() }])),
      );
    });
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((row) => {
      const rowStatus = String(row.status || "").toLowerCase();
      if (statusFilter !== "all" && rowStatus !== statusFilter) return false;
      if (!q) return true;
      return [
        row.id,
        row.packageId,
        row.packageSnapshot?.name,
        row.instructorId,
        row.instructorName,
        row.instructorEmail,
        row.instructorSnapshot?.name,
        row.instructorSnapshot?.email,
        shopLabel(row, userMap),
        shopEmail(row, userMap),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [orders, search, statusFilter, userMap]);

  const stats = useMemo(() => {
    return filtered.reduce(
      (acc, row) => {
        const amount = n(row.packageSnapshot?.price);
        const affiliateIncome = n(
          row.affiliateIncomeAmount ?? row.payoutSnapshot?.affiliatePayoutApplied,
        );
        const developerIncome = n(
          row.developerIncomeAmount ?? row.payoutSnapshot?.developerPayoutApplied,
        );
        const payoutType = normalizePayoutType(
          row.payoutSnapshot?.affiliatePayoutType,
        );

        acc.totalOrders += 1;
        if (row.status === "pending") acc.pendingOrders += 1;
        if (row.status === "approved") {
          acc.approvedOrders += 1;
          acc.approvedRevenue += amount;
          acc.affiliateIncome += affiliateIncome;
          acc.developerIncome += developerIncome;
          if (payoutType === "renewal") acc.renewalOrders += 1;
          else acc.firstSaleOrders += 1;
        }
        return acc;
      },
      {
        totalOrders: 0,
        pendingOrders: 0,
        approvedOrders: 0,
        approvedRevenue: 0,
        affiliateIncome: 0,
        developerIncome: 0,
        firstSaleOrders: 0,
        renewalOrders: 0,
      },
    );
  }, [filtered]);

  const packageRows = useMemo(() => {
    const grouped = new Map();
    filtered
      .filter((row) => row.status === "approved")
      .forEach((row) => {
        const key = row.packageId || row.packageSnapshot?.name || row.id;
        const existing = grouped.get(key) || {
          key,
          name: row.packageSnapshot?.name || row.packageId || "Unnamed package",
          orders: 0,
          revenue: 0,
          affiliateIncome: 0,
          developerIncome: 0,
        };
        existing.orders += 1;
        existing.revenue += n(row.packageSnapshot?.price);
        existing.affiliateIncome += n(
          row.affiliateIncomeAmount ?? row.payoutSnapshot?.affiliatePayoutApplied,
        );
        existing.developerIncome += n(
          row.developerIncomeAmount ?? row.payoutSnapshot?.developerPayoutApplied,
        );
        grouped.set(key, existing);
      });

    return Array.from(grouped.values()).sort((a, b) => b.revenue - a.revenue);
  }, [filtered]);

  return (
    <div className="space-y-6 p-6">
      <div className="rounded-[32px] border border-amber-200 bg-gradient-to-br from-[#fff8ee] via-white to-[#f3ebe0] p-6 shadow-sm md:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex rounded-full border border-amber-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">
              Shop Package Sales
            </div>
            <h1 className="mt-4 text-3xl font-black tracking-tight text-slate-900 md:text-4xl">
              Package sales summaries, referral income, and order details
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 md:text-base">
              This page now focuses on shop package sales only, including what each package earned, how much affiliate income went out, and the developer income kept on approved package orders.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search package, shop owner, UID..."
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none ring-0 sm:w-72"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard label="Approved Revenue" value={money(stats.approvedRevenue)} tone="text-slate-900" />
        <StatCard label="Affiliate Income" value={money(stats.affiliateIncome)} tone="text-emerald-700" />
        <StatCard label="Developer Income" value={money(stats.developerIncome)} tone="text-indigo-700" />
        <StatCard label="1st Sale Orders" value={stats.firstSaleOrders} helper={`Renewals: ${stats.renewalOrders}`} />
        <StatCard label="Pending Orders" value={stats.pendingOrders} helper={`Total loaded: ${stats.totalOrders}`} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr,1.05fr]">
        <div className="rounded-3xl border bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-black text-slate-900">Package Performance</h2>
              <p className="mt-1 text-sm text-slate-500">
                Approved shop package sales grouped by package.
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {packageRows.map((row) => (
              <div key={row.key} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-lg font-bold text-slate-900">{row.name}</div>
                    <div className="mt-1 text-sm text-slate-500">{row.orders} approved order{row.orders === 1 ? "" : "s"}</div>
                  </div>
                  <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                    Revenue {money(row.revenue)}
                  </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-white px-4 py-3">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Affiliate Income</div>
                    <div className="mt-1 font-bold text-emerald-700">{money(row.affiliateIncome)}</div>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Developer Income</div>
                    <div className="mt-1 font-bold text-indigo-700">{money(row.developerIncome)}</div>
                  </div>
                </div>
              </div>
            ))}

            {packageRows.length === 0 ? (
              <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-slate-500">
                No approved package sales found for the current filter.
              </div>
            ) : null}
          </div>
        </div>

        <div className="rounded-3xl border bg-white p-5 shadow-sm">
          <h2 className="text-xl font-black text-slate-900">Package Order Details</h2>
          <p className="mt-1 text-sm text-slate-500">
            Every shop package order with referral income and developer income attached.
          </p>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="px-3 py-3 font-semibold">Shop Owner</th>
                  <th className="px-3 py-3 font-semibold">Package</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                  <th className="px-3 py-3 font-semibold">Type</th>
                  <th className="px-3 py-3 font-semibold text-right">Amount</th>
                  <th className="px-3 py-3 font-semibold text-right">Affiliate</th>
                  <th className="px-3 py-3 font-semibold text-right">Developer</th>
                  <th className="px-3 py-3 font-semibold">Referral</th>
                  <th className="px-3 py-3 font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const payoutType = normalizePayoutType(
                    row.payoutSnapshot?.affiliatePayoutType,
                  );
                  const linked = Boolean(
                    row.referralSnapshot?.affiliateUid ||
                      row.referralSnapshot?.affiliateReferralCode,
                  );
                  return (
                    <tr key={row.id} className="border-b last:border-b-0">
                      <td className="px-3 py-3 align-top">
                        <div className="font-semibold text-slate-900">
                          {shopLabel(row, userMap)}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {shopEmail(row, userMap) || row.instructorId}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="font-semibold text-slate-900">
                          {row.packageSnapshot?.name || row.packageId}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {row.packageId || row.id}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone(row.status)}`}>
                          {row.status || "unknown"}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${payoutTone(payoutType)}`}>
                          {payoutType === "renewal" ? "Renewal" : "1st Sale"}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-slate-900">
                        {money(row.packageSnapshot?.price)}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-emerald-700">
                        {money(
                          row.affiliateIncomeAmount ??
                            row.payoutSnapshot?.affiliatePayoutApplied,
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-indigo-700">
                        {money(
                          row.developerIncomeAmount ??
                            row.payoutSnapshot?.developerPayoutApplied,
                        )}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            linked
                              ? "bg-green-100 text-green-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {linked ? "Linked" : "No link"}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top text-slate-500">
                        {fmtDate(row.createdAt)}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                      No package orders found for this filter.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
